import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import groupToggleIcon from '../assets/group-toggle.svg';
import { apiClient } from '@/lib/api';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { useAuthStore } from '@store/authStore';
import {
  RiFileList3Line,
  RiDownloadLine,
  RiMoneyDollarCircleLine,
  RiLineChartLine,
  RiTeamLine,
  RiPercentLine,
  RiFilterLine,
  RiExpandUpDownFill,
  RiCloseLine,
  RiLoader4Line,
  RiSearchLine,
  RiFlightTakeoffLine,
} from '@remixicon/react';
import { Card, CardContent } from '@components/ui/Card';
import { Checkbox } from '@components/ui/Checkbox';
import { Skeleton } from '@components/ui/Skeleton';
import { usePermissions } from '@/hooks/usePermissions';
import { MODULES, SCOPES } from '@/lib/permissions';
import { Tabs, TabsList, TabsTrigger } from '@components/ui/Tabs';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from '@components/ui/DropdownMenu';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@components/ui/Table';
import { formatPrice, trunc2 } from '@/utils';
import { QuoteDetailsDrawer } from '@/features/quotes/components';
import AdvanceInvoiceDrawer from '@/features/quotes/components/AdvanceInvoiceDrawer';
import { getQuoteStatusConfig, getEffectiveQuoteStatus } from '@/features/quotes/utils';
import { getEffectiveAircraft, getEffectiveOperatorName } from '@/features/tails/utils';
import { useAirportSearch } from '@/features/quotes/services/airportService';
import FinancialsDateRangeModal from '../components/FinancialsDateRangeModal';

// One source of truth for column rendering, sort, and filter type. The order
// here drives the header row, the body cells, and the CSV export.
// (SORT_OPTIONS / SORT_PRESETS were removed with the standalone Sort By
// dropdown — per-column header sort is the only sort UI now.)
// Payment status enum — options exposed inside the Payment Status column
// filter (single-select radio list). Mirrors the BE `quote_payment_status`
// enum on gtj-dev exactly. Do NOT add invoice_status values (DRAFT / SENT
// / PAID / VOID) here — those live on a different column and would be
// dead options against the `payment_status_search` param, which only
// ILIKE-matches quotes.payment_status / invoices.payment_status text
// (see api.js /financial/reports docs).
const PAYMENT_STATUSES = [
  'CAPTURED',
  'SETTLED_VIA_PASSPORT',
  'WIRE_TRANSFER_IN_PROGRESS',
  'PRE_AUTHORIZED',
  'AUTHORIZED',
  'CANCELLED',
  'REJECTED',
];

// Column categories drive the grouped header. `always` columns are
// pinned (no collapse); the other three groups collapse/expand as a
// unit via the pill button in the header (see GroupHeader).
// Order below is the render order left-to-right and matches the
// Financials restructure ticket exactly.
//
// `singleSelect` on a `type: 'status'` column forces the filter body
// into radio-style single-selection — used for payment_status because
// the BE only exposes an ILIKE substring param (`payment_status_search`)
// that can't accept a comma-separated multi-value list.
const COLUMNS = [
  // Always Visible
  { key: 'quote_number', label: 'Trip ID', type: 'text', category: 'always' },
  { key: 'client_name', label: 'Client Name', type: 'text', category: 'always' },
  { key: 'departure_date', label: 'Next Flight Date', type: 'date', category: 'always' },
  { key: 'route', label: 'Route', type: 'airport', category: 'always' },
  { key: 'total_due', label: 'Total Amount', type: 'number', category: 'always' },
  { key: 'payment_status', label: 'Payment Status', type: 'status', singleSelect: true, category: 'always' },

  // Client Details — collapsible group anchored on Client Name.
  { key: 'company_name', label: 'Company Name', type: 'text', category: 'client' },

  // Flight Details
  { key: 'operator_name', label: 'Operator Name', type: 'text', category: 'flight' },
  { key: 'tail_number', label: 'Tail Number', type: 'text', category: 'flight' },
  { key: 'trip_legs', label: 'Trip Legs', type: 'text', category: 'flight' },

  // Cost Breakdown
  { key: 'operator_cost', label: 'Operator Cost', type: 'number', category: 'cost' },
  { key: 'broker_margin', label: 'Broker Margin', type: 'number', category: 'cost' },
  { key: 'broker_margin_percent', label: 'Broker Margin (%)', type: 'number', category: 'cost' },
  { key: 'total_taxable_amount', label: 'Total Taxable Amount', type: 'number', category: 'cost' },
  { key: 'federal_excise_tax', label: 'FET on Trip', type: 'number', category: 'cost' },
  { key: 'segment_tax', label: 'Segment Fee', type: 'number', category: 'cost' },
  // International Tax — BE field not yet shipped. Renders "N/A" until
  // BE populates row.international_tax. Positioned beside the other
  // tax columns per the ticket layout.
  { key: 'international_tax', label: 'International Tax', type: 'number', category: 'cost' },
  { key: 'total_taxes', label: 'Total Taxes', type: 'number', category: 'cost' },
  { key: 'card_fee', label: 'Card Fees', type: 'number', category: 'cost' },
  // Amount Paid to Broker — FE-computed:
  //   total_amount − platform_fee − card_fee (both null-safe).
  { key: 'amount_paid_to_broker', label: 'Amount Paid to Broker', type: 'number', category: 'cost' },

  // Quote Tracking
  { key: 'quote_status', label: 'Quote Status', type: 'status', category: 'tracking' },
  { key: 'date_quote_sent', label: 'Date Quote Sent', type: 'text', category: 'tracking' },
  // Date Signed — pulled from quote.files.contract_signed_date, written
  // when the client submits the signed contract on QuotePreviewPage.
  // Invoice-typed rows (advance / additional invoices) never carry a
  // signed contract, so they render as '-'.
  { key: 'date_signed', label: 'Date Signed', type: 'text', category: 'tracking' },
  { key: 'date_paid', label: 'Date Paid', type: 'text', category: 'tracking' },
  // Created By replaces the old broker_name column (which was creator-
  // only after the last QA revision). Sent By is a new sibling that
  // surfaces the delegated-send case (creator ≠ sender). BE
  // `sender_search` isn't available yet, so Sent By has no filter icon.
  { key: 'created_by', label: 'Created By', type: 'text', category: 'tracking' },
  { key: 'sent_by', label: 'Sent By', type: 'text', category: 'tracking' },
];

// The three collapsible groups, in render order. `always` is
// pinned and doesn't appear here. Labels come from the ticket copy.
const COLUMN_GROUPS = [
  { key: 'client', label: 'Client Details' },
  { key: 'flight', label: 'Flight Details' },
  { key: 'cost', label: 'Cost Breakdown' },
  { key: 'tracking', label: 'Quote Tracking' },
];

// Column key → list of selectable status values in that column's filter.
// quote_status uses the REPORTABLE_STATUSES set (revenue statuses only —
// matches the standalone Status dropdown this filter replaces);
// payment_status uses PAYMENT_STATUSES. Returns [] for non-status
// columns so callers can safely early-return.
const getStatusOptions = (colKey) => {
  if (colKey === 'quote_status') return REPORTABLE_STATUSES;
  if (colKey === 'payment_status') return PAYMENT_STATUSES;
  return [];
};

const isFilterActive = (filter) => {
  if (!filter) return false;
  if (filter.type === 'text' || filter.type === 'airport') return !!filter.value;
  if (filter.type === 'number') return (filter.min !== '' && filter.min != null) || (filter.max !== '' && filter.max != null);
  if (filter.type === 'date') return !!filter.start || !!filter.end;
  if (filter.type === 'status') return Array.isArray(filter.values) && filter.values.length > 0;
  return false;
};

/**
 * AirportInput
 * Route-column body used inside FilterInputs. Text input + inline typeahead
 * dropdown backed by the BE `search=` param via useAirportSearch — fuzzy
 * ILIKE across code, icao, name, city, state, country. Draft state lives
 * on the parent FilterInputs; this component just reads/writes it via
 * setDraft. Picking a suggestion auto-commits via `onImmediateCommit`, so
 * the user doesn't have to click Apply again after choosing an airport.
 *
 * Min length 2 (matches BE gate). No more "type a full ICAO code to see
 * anything" — the user can type "los ang" and see KLAX, or "kte" and see
 * KTEB as they type.
 */
const AirportInput = ({ draft, setDraft, onImmediateCommit, inputProps }) => {
  const text = draft?.value || '';
  const [debouncedText, setDebouncedText] = useState('');

  // Short 300ms debounce so we don't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedText(text.trim()), 300);
    return () => clearTimeout(t);
  }, [text]);

  const { data: results = [], isFetching } = useAirportSearch(debouncedText, { limit: 30 });
  // Only show the dropdown once the user has typed enough to trigger the
  // hook — otherwise we'd render an empty results card on every focus.
  const showDropdown = debouncedText.length >= 2;

  const handleChange = (e) => {
    const v = e.target.value;
    setDraft(v ? { type: 'airport', value: v, label: '' } : null);
  };

  const handlePick = (airport) => {
    if (!airport) return;
    const code = airport.icao || airport.code || debouncedText;
    // Picking a suggestion IS the Apply action — commit + close.
    onImmediateCommit({ type: 'airport', value: code, label: airport.name || '' });
  };

  return (
    <div className="flex flex-col gap-2">
      <label className="text-xs font-medium text-gray-600">Airport (code, city, name…)</label>
      <input
        type="text"
        value={text}
        onChange={handleChange}
        placeholder="e.g. KTEB, Teterboro, Miami"
        className="px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 placeholder:text-gray-400"
        autoFocus
        {...inputProps}
      />
      {/* Inline suggestion list — takes up flex space instead of floating.
          Radix's Floating UI positioner grows the enclosing DropdownMenu
          and flips it above the trigger if there's not enough space
          below, so the user never has to scroll to see the results. */}
      {showDropdown && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden max-h-56 overflow-y-auto">
          {isFetching && results.length === 0 ? (
            <div className="flex items-center gap-2 px-3 py-2.5 text-sm text-gray-500">
              <RiLoader4Line className="w-4 h-4 animate-spin text-gray-400" />
              Searching…
            </div>
          ) : results.length > 0 ? (
            results.map((airport) => {
              const code = airport.icao || airport.code || '';
              return (
                <button
                  key={`${code}-${airport.name || ''}`}
                  type="button"
                  onClick={() => handlePick(airport)}
                  className="w-full text-left px-3 py-2.5 hover:bg-primary-50 flex items-start gap-3 focus:outline-none focus:bg-primary-50 border-b border-gray-100 last:border-b-0"
                >
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                    <RiFlightTakeoffLine className="w-4 h-4 text-primary-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-gray-900 tracking-wide">
                      {code || airport.name}
                    </div>
                    {(airport.name || airport.city) && (
                      <div className="text-xs text-gray-500 truncate">
                        {[airport.name, airport.city, airport.country].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </div>
                </button>
              );
            })
          ) : (
            <div className="px-3 py-2.5 text-xs text-gray-500">
              No airports match <span className="font-medium text-gray-700">"{debouncedText}"</span>.
              Substring search still applies on Apply.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * FilterInputs
 * Body of a column-header filter dropdown. Holds a local draft mirror of
 * the currently-committed `filter`; the draft is committed via onCommit
 * only when the user clicks Apply (or presses Enter, or picks a typeahead
 * suggestion). Cancel closes the dropdown without touching the parent's
 * committed state; Clear commits null and closes.
 */
const FilterInputs = ({ col, filter, onCommit, onCancel }) => {
  const [draft, setDraft] = useState(filter || null);

  // Re-seed the draft whenever the dropdown re-opens with a different
  // committed value (e.g. chip × cleared this filter externally). This
  // effect runs on mount too — that's fine, first render seeds from the
  // initial `filter` prop, matching the committed state.
  useEffect(() => { setDraft(filter || null); }, [filter]);

  const stopInput = (e) => e.stopPropagation();
  const inputProps = {
    onKeyDown: (e) => {
      stopInput(e);
      if (e.key === 'Enter') {
        e.preventDefault();
        onCommit(isFilterActive(draft) ? draft : null);
      }
    },
    onKeyUp: stopInput,
    onPaste: stopInput,
  };

  const handleApply = () => {
    onCommit(isFilterActive(draft) ? draft : null);
  };
  const handleCancel = () => {
    setDraft(filter || null);
    onCancel();
  };
  const handleClear = () => {
    setDraft(null);
    onCommit(null);
  };

  let body = null;
  if (col.type === 'airport') {
    body = (
      <AirportInput
        draft={draft}
        setDraft={setDraft}
        onImmediateCommit={onCommit}
        inputProps={inputProps}
      />
    );
  } else if (col.type === 'text') {
    body = (
      <div className="flex flex-col gap-2">
        <label className="text-xs font-medium text-gray-600">Contains</label>
        <input
          type="text"
          value={draft?.value || ''}
          onChange={(e) => setDraft(e.target.value ? { type: 'text', value: e.target.value } : null)}
          className="px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
          autoFocus
          {...inputProps}
        />
      </div>
    );
  } else if (col.type === 'number') {
    body = (
      <div className="flex flex-col gap-2">
        <label className="text-xs font-medium text-gray-600">Range</label>
        <div className="flex gap-2">
          <input
            type="number"
            placeholder="Min"
            value={draft?.min ?? ''}
            onChange={(e) => setDraft({ type: 'number', min: e.target.value, max: draft?.max ?? '' })}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            {...inputProps}
          />
          <input
            type="number"
            placeholder="Max"
            value={draft?.max ?? ''}
            onChange={(e) => setDraft({ type: 'number', min: draft?.min ?? '', max: e.target.value })}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            {...inputProps}
          />
        </div>
      </div>
    );
  } else if (col.type === 'date') {
    body = (
      <div className="flex flex-col gap-2">
        <label className="text-xs font-medium text-gray-600">Date range</label>
        <input
          type="date"
          value={draft?.start || ''}
          onChange={(e) => setDraft({ type: 'date', start: e.target.value, end: draft?.end || '' })}
          className="px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
          {...inputProps}
        />
        <input
          type="date"
          value={draft?.end || ''}
          onChange={(e) => setDraft({ type: 'date', start: draft?.start || '', end: e.target.value })}
          className="px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
          {...inputProps}
        />
      </div>
    );
  } else if (col.type === 'status') {
    // Checkbox (multi) OR radio (single) list — same stored shape
    // (`{ type: 'status', values: string[] }`) either way. Options come
    // from getStatusOptions keyed by col.key.
    //   Multi (default, quote_status): sends comma-separated exact
    //     matches on the `status` param — every checked value narrows.
    //   Single (col.singleSelect, payment_status): sends the single
    //     value as ILIKE substring on `payment_status_search`. Radio
    //     UI enforces the constraint that only one value can travel on
    //     that wire param.
    const options = getStatusOptions(col.key);
    const selected = Array.isArray(draft?.values) ? draft.values : [];
    const single = !!col.singleSelect;
    const pickOne = (v) => {
      setDraft({ type: 'status', values: [v] });
    };
    const toggle = (v) => {
      const next = selected.includes(v)
        ? selected.filter((x) => x !== v)
        : [...selected, v];
      setDraft(next.length ? { type: 'status', values: next } : null);
    };
    const allChecked = !single && selected.length === options.length && options.length > 0;
    const toggleAll = () => {
      setDraft(allChecked ? null : { type: 'status', values: [...options] });
    };
    body = (
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <label className="text-xs font-medium text-gray-600">
            {single ? 'Select a status' : 'Select statuses'}
          </label>
          {!single && (
            <button
              type="button"
              onClick={toggleAll}
              className="text-xs text-primary-600 hover:text-primary-700 hover:underline"
            >
              {allChecked ? 'Clear all' : 'Select all'}
            </button>
          )}
        </div>
        <div className="max-h-56 overflow-y-auto flex flex-col gap-1 pr-1">
          {options.map((opt) => {
            const isSelected = selected.includes(opt);
            return (
              <label key={opt} className="flex items-center gap-2 px-1 py-1 text-sm text-gray-700 hover:bg-gray-50 rounded cursor-pointer">
                <input
                  type={single ? 'radio' : 'checkbox'}
                  name={single ? `status-radio-${col.key}` : undefined}
                  checked={isSelected}
                  onChange={() => (single ? pickOne(opt) : toggle(opt))}
                  className={single
                    ? 'border-gray-300 text-primary-600 focus:ring-primary-500'
                    : 'rounded border-gray-300 text-primary-600 focus:ring-primary-500'}
                />
                <span>{opt.replace(/_/g, ' ')}</span>
              </label>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {body}
      {/* Footer: Clear on the left when a filter is currently committed
          (removes it), Cancel + Apply on the right. Apply commits the
          draft even when empty (which is equivalent to Clear when the
          user wiped an existing value). */}
      <div className="flex items-center justify-between gap-2 pt-2 border-t border-gray-100">
        {isFilterActive(filter) ? (
          <button
            type="button"
            onClick={handleClear}
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            Clear
          </button>
        ) : <span />}
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={handleCancel}
            className="px-3 py-1.5 text-xs text-gray-700 rounded-full hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleApply}
            className="px-4 py-1.5 text-xs text-white bg-primary-700 rounded-full hover:bg-primary-800"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

const ColumnHeader = ({ col, sort, onSort, filter, onFilterChange, serverFilterable, serverSortable }) => {
  const isSorted = sort?.key === col.key;
  const SortIcon = RiExpandUpDownFill;
  const filterOn = isFilterActive(filter);
  // Controlled open state so FilterInputs can programmatically close the
  // dropdown on Apply / Cancel (Radix Dropdown doesn't expose a native
  // "close from inside" hook other than pressing Escape).
  const [filterOpen, setFilterOpen] = useState(false);
  return (
    <div className="flex items-center gap-1 justify-between">
      {/* Label doubles as a sort trigger only when the column is
          server-sortable. Non-sortable labels render as plain text with
          the default cursor — no hover state, no click. */}
      {serverSortable ? (
        <button
          type="button"
          onClick={() => onSort(col.key)}
          className="hover:text-gray-900 focus:outline-none truncate"
        >
          {col.label}
        </button>
      ) : (
        <span className="truncate">{col.label}</span>
      )}
      <div className="flex items-center gap-1 shrink-0">
        {/* Filter icon renders only for columns the BE can actually filter
            on. Columns without a *_search / min_/max_ / *_from|to param
            (trip_legs, operator_cost, total_taxable_amount, date_paid,
            and the created_at column that's owned by the top-level
            Date tabs) intentionally have no filter here — a client-
            side-only filter over the loaded pages would mislead once
            server pagination is in play. */}
        {serverFilterable && (
          <DropdownMenu open={filterOpen} onOpenChange={setFilterOpen}>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className={`p-1 rounded hover:bg-gray-200 ${filterOn ? 'text-primary-600' : 'text-gray-400'}`}
                aria-label={`Filter ${col.label}`}
              >
                <RiFilterLine className="w-3.5 h-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72 p-3">
              <FilterInputs
                col={col}
                filter={filter}
                // Commit path — propagate the new value up AND close the
                // dropdown. Fires on Apply, Enter, Clear, or a picked
                // airport suggestion (AirportInput's onImmediateCommit).
                onCommit={(nextFilter) => {
                  onFilterChange(nextFilter);
                  setFilterOpen(false);
                }}
                onCancel={() => setFilterOpen(false)}
              />
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {/* Sort icon hidden on non-server-sortable columns — same reason as
            the filter icon: a client-only sort over just the loaded pages
            would order 50 rows when 4,000 match, and that's misleading. */}
        {serverSortable && (
          <button
            type="button"
            onClick={() => onSort(col.key)}
            className="p-1 rounded hover:bg-gray-200 focus:outline-none"
            aria-label={`Sort ${col.label}`}
          >
            <SortIcon className={`w-3.5 h-3.5 ${isSorted ? 'text-gray-700' : 'text-gray-400'}`} />
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * GroupToggleButton
 * Small square button that toggles a column group's expand/collapse.
 * Rendered inline beside the anchor column header (Route / Total Amount
 * / Payment Status) so there's no separate category header row — each
 * group is opened from the always-visible column that semantically
 * "leads into" it. Icon = Vector.svg, rotated 180° when the group is
 * expanded so a single asset drives both directions.
 */
const GroupToggleButton = ({ expanded, groupLabel, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-label={`${expanded ? 'Collapse' : 'Expand'} ${groupLabel}`}
    aria-expanded={expanded}
    className="w-6 h-6 inline-flex items-center justify-center bg-white border hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 flex-shrink-0"
    style={{ borderColor: '#EBEBEB', borderRadius: '6px' }}
  >
    <img
      src={groupToggleIcon}
      alt=""
      aria-hidden="true"
      className="w-3 h-3 transition-transform duration-200 ease-out"
      style={{ transform: expanded ? 'rotate(180deg)' : 'none' }}
    />
  </button>
);

// Statuses considered real revenue for tax reporting. Sent as a
// comma-separated `status=` query param — the server ORs across quote AND
// invoice statuses per row, so `PAID` covers standalone advance invoices
// that don't carry a quote status.
// CANCELLED is intentionally NOT here so the summary KPI aggregates
// (total revenue, commissions, closed-ratio, etc.) ignore cancelled
// trips. The transactions list separately opts CANCELLED back in via
// LIST_EXTRA_STATUSES so cancelled rows still appear on the table
// for audit, zeroed via mapRowToDisplay's isCancelledTrip override.
const REPORTABLE_STATUSES = [
  'SENT',
  'VIEWED',
  'SIGNED',
  'ACCEPTED',
  'TRIPSHEET_PENDING_APPROVAL',
  'PASSENGERS_PENDING',
  'WAITING_FLIGHT',
  'IN_TRANSIT',
  'COMPLETED',
  'DIVERTED',
  'PAID',
];
// Statuses the transactions list adds on top of REPORTABLE_STATUSES
// — these are shown but not counted in the summary totals.
const LIST_EXTRA_STATUSES = ['CANCELLED'];

// Server-side page size for the transactions endpoint. BE caps at 200; 50
// keeps the initial paint fast without making Load More feel too grabby.
const PAGE_SIZE = 50;

// Column keys → BE sort_by values. Any column key not in this map is not
// server-sortable — header sort falls back to a client-side sort over just
// the loaded pages, which is a documented within-page refinement.
// `departure_date` (column labeled "Next Flight Date") maps to
// `next_flight_date` — the derived field on bid.leg_itinerary[0], which is
// the semantic the label promises.
const SERVER_SORT_MAP = {
  // Column displays sent_at (see mapRowToDisplay) — sort by the same
  // field so the arrow ordering matches the values the user sees.
  date_quote_sent: 'sent_at',
  departure_date: 'next_flight_date',
  total_taxes: 'total_taxes',
  total_due: 'total_amount',
  client_name: 'client_name',
};

// Column keys → server-side text filter params. When a column key is present
// here, the ColumnHeader renders a filter icon that pipes user input into
// the corresponding server-side ILIKE search (min 2 chars). Absent columns
// are treated as "not server-filterable" and get no filter icon.
const SERVER_TEXT_FILTER = {
  quote_number:   'reference_no_search',
  client_name:    'client_search',
  route:          'airport_search',
  operator_name:  'operator_search',
  tail_number:    'tail_search',
  // The old `broker_name` column (creator-only after the QA revision)
  // was split into Created By + Sent By. Only Created By keeps the
  // wire filter — BE doesn't expose a sender_search param yet, so
  // Sent By column has no filter icon.
  created_by:     'creator_search',
  // NOTE: quote_status + payment_status used to be `*_search` ILIKE
  // fields here. They're now multi-select (see SERVER_STATUS_FILTER
  // below) so users pick from an enum list instead of typing free text.
};

// Column keys → server-side status filter params.
//   quote_status   → `status_search`: comma-separated exact-match
//     enum list against quote + invoice statuses.
//   payment_status → `payment_status_search`: single value, ILIKE
//     substring (BE min 2 chars). Multi-select isn't possible on this
//     param, hence payment_status column uses `singleSelect: true`.
// The `_search` suffix alone no longer determines the wire encoding —
// only params in SINGLE_VALUE_STATUS_PARAMS get single-value encoding;
// everything else goes comma-separated. Keeps status_search multi
// while payment_status_search stays single.
const SERVER_STATUS_FILTER = {
  quote_status:   'status_search',
  payment_status: 'payment_status_search',
};

// Status filter params that only accept a single value on the wire
// (BE limitation). Everything else in SERVER_STATUS_FILTER is
// encoded as a comma-separated list.
const SINGLE_VALUE_STATUS_PARAMS = new Set(['payment_status_search']);

// Column keys → [min_param, max_param] for numeric range filters. Same idea
// as SERVER_TEXT_FILTER: presence enables the filter icon; absence hides it.
const SERVER_NUMBER_FILTER = {
  broker_margin:         ['min_broker_margin',         'max_broker_margin'],
  broker_margin_percent: ['min_broker_margin_percent', 'max_broker_margin_percent'],
  federal_excise_tax:    ['min_federal_excise_tax',    'max_federal_excise_tax'],
  segment_tax:           ['min_segment_tax',           'max_segment_tax'],
  card_fee:              ['min_card_fee',              'max_card_fee'],
  total_taxes:           ['min_taxes',                 'max_taxes'],
  total_due:             ['min_amount',                'max_amount'],
};

// Column keys → [from_param, to_param] for date range filters. Same rule.
// Note: the `date_quote_sent` (created_at) column deliberately isn't listed
// here — the top-level Date tabs (All/Custom) already own
// `created_at_from/to`, so a header filter on that column would double-book.
const SERVER_DATE_FILTER = {
  departure_date: ['next_flight_date_from', 'next_flight_date_to'],
};

// Preset id → display label. Mirrors the PRESETS array inside
// FinancialsDateRangeModal so the Custom tab and the active-filter chip
// can echo the preset name the user picked (e.g. "Last 30 days") instead
// of the resolved raw dates. `custom` is the freeform-calendar case and
// has no preset label — those callsites fall back to `from → to`.
const PRESET_LABELS = {
  today: 'Today',
  last7: 'Last 7 days',
  last30: 'Last 30 days',
  last3mo: 'Last 3 months',
  last12mo: 'Last 12 months',
  mtd: 'Month to date',
  ytd: 'Year to date',
};

// Fast lookup: does a column key have ANY server-side filter support?
const isColumnServerFilterable = (key) =>
  Object.prototype.hasOwnProperty.call(SERVER_TEXT_FILTER, key)
  || Object.prototype.hasOwnProperty.call(SERVER_NUMBER_FILTER, key)
  || Object.prototype.hasOwnProperty.call(SERVER_DATE_FILTER, key)
  || Object.prototype.hasOwnProperty.call(SERVER_STATUS_FILTER, key);

// Columns the FE lets the user sort *without* server support — the memo in
// sortedData handles ordering across the loaded pages. Reserved for columns
// whose value lives somewhere the BE doesn't sort on (e.g. nested JSON like
// quote.files.contract_signed_date) but where a header sort is still useful.
// Values here are ISO date strings, so the memo's localeCompare fallback
// orders them chronologically without needing a 'date' col.type change.
const CLIENT_ONLY_SORT_KEYS = new Set(['date_signed', 'date_paid']);

// Same idea for sort — hide the sort icon on columns the BE can't sort by
// so we don't offer an action that only orders the loaded pages, unless
// the column is explicitly opted in via CLIENT_ONLY_SORT_KEYS.
const isColumnServerSortable = (key) =>
  Object.prototype.hasOwnProperty.call(SERVER_SORT_MAP, key)
  || CLIENT_ONLY_SORT_KEYS.has(key);

const compactCurrency = (value) => {
  const num = Number(value) || 0;
  const abs = Math.abs(num);
  if (abs >= 1_000_000) return `$${(num / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 1_000) return `$${(num / 1_000).toFixed(abs >= 100_000 ? 0 : 1)}k`;
  return `$${num.toFixed(0)}`;
};

const KpiCard = ({ label, value, subtitle, Icon, iconBg, iconColor, valueSize = 'text-2xl' }) => (
  <Card className="h-full !rounded-3xl">
    <CardContent className="p-4 h-full">
      <div className="flex flex-col h-full">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col min-w-0">
            {subtitle && <span className="text-xs font-medium text-gray-500">{label}</span>}
            <span className={`${valueSize} font-semibold text-gray-900 ${subtitle ? 'mt-1' : ''} truncate`}>{value}</span>
            {subtitle && <span className="text-xs text-gray-500 mt-1">{subtitle}</span>}
          </div>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${iconBg}`}>
            <Icon className={`w-4 h-4 ${iconColor}`} />
          </div>
        </div>
        {!subtitle && (
          <span className="text-xs font-medium text-gray-500 mt-auto pt-3">{label}</span>
        )}
      </div>
    </CardContent>
  </Card>
);

/**
 * TaxReports Page
 * Displays tax information from quotes in a datatable format.
 */
const TaxReports = () => {

  const { userProfile, isLoading: isAuthLoading, isAuthenticated, fetchUserProfile } = useAuthStore();

  // timeWindow: 'all' | 'custom'.
  // Only two toolbar tabs — everything else (Last 30 Days, Month to Date,
  // Year to Date, freeform range, etc.) is a preset inside the
  // FinancialsDateRangeModal that resolves to `customFrom`/`customTo` and
  // flips this into 'custom'. QA feedback: don't duplicate the Monthly /
  // Yearly presets as top-level tabs when Custom already offers them.
  const [timeWindow, setTimeWindow] = useState('all');
  // Applied Custom-range values (only used when timeWindow === 'custom').
  // Stored as YYYY-MM-DD strings — same shape the BE takes.
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [isCustomDateOpen, setIsCustomDateOpen] = useState(false);
  // Preset id inside the modal that produced the current tab state, or null
  // when the user picked freeform dates on the calendar. Used only to seed
  // the modal's sidebar highlight the next time it opens.
  const [modalPresetId, setModalPresetId] = useState('all');

  const [columnSort, setColumnSort] = useState({ key: 'date_quote_sent', dir: 'desc' });
  const [columnFilters, setColumnFilters] = useState({});
  // Collapse state for the three column groups (Flight Details, Cost
  // Breakdown, Quote Tracking). Persisted per browser so a broker's
  // last expand/collapse choice survives page reloads. Initial state
  // is all-collapsed per QA — a fresh visitor gets the tightest
  // signal-first view.
  const [groupsExpanded, setGroupsExpanded] = useState(() => {
    try {
      const raw = window.localStorage.getItem('financials.groups');
      if (raw) {
        const parsed = JSON.parse(raw);
        return {
          client: !!parsed?.client,
          flight: !!parsed?.flight,
          cost: !!parsed?.cost,
          tracking: !!parsed?.tracking,
        };
      }
    } catch { /* localStorage disabled or JSON corrupt — fall through */ }
    return { client: false, flight: false, cost: false, tracking: false };
  });
  useEffect(() => {
    try {
      window.localStorage.setItem('financials.groups', JSON.stringify(groupsExpanded));
    } catch { /* ignore — non-fatal */ }
  }, [groupsExpanded]);
  const toggleGroup = useCallback((groupKey) => {
    setGroupsExpanded((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
  }, []);

  // Derived column partitions — computed once per COLUMNS/groupsExpanded
  // change and reused by header + body renderers so cell counts always
  // agree.
  const alwaysCols = useMemo(() => COLUMNS.filter(c => c.category === 'always'), []);
  // Full-width cell count including the checkbox column — used for
  // colSpan on the loading / error / empty-state rows so they span the
  // whole table regardless of which groups are open. Collapsed groups
  // contribute NO cells now (their toggle lives inline in the anchor
  // column header, no spacer needed).
  const totalCellCount = useMemo(() => {
    let n = 1 /* checkbox */ + alwaysCols.length;
    COLUMN_GROUPS.forEach(g => {
      if (groupsExpanded[g.key]) {
        n += COLUMNS.filter(c => c.category === g.key).length;
      }
    });
    return n;
  }, [alwaysCols.length, groupsExpanded]);
  const [globalFilter, setGlobalFilter] = useState('');
  // Debounced copy of the global search — search feeds `search=` on the
  // server, so we let the user finish typing before refetching.
  const [globalFilterDebounced, setGlobalFilterDebounced] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [drawerQuoteId, setDrawerQuoteId] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [invoiceDrawerData, setInvoiceDrawerData] = useState(null);
  const [invoiceDrawerId, setInvoiceDrawerId] = useState(null);
  const [invoiceDrawerStatus, setInvoiceDrawerStatus] = useState(null);
  const [isInvoiceDrawerOpen, setIsInvoiceDrawerOpen] = useState(false);
  // (Standalone Broker filter state removed — broker filtering now lives
  // in the Broker Name column filter via creator_search ILIKE.)
  // CSV export state — an unpaged fetch takes a beat on a large filter set.
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    if (isAuthenticated && !userProfile && !isAuthLoading) {
      fetchUserProfile();
    }
  }, [isAuthenticated, userProfile, isAuthLoading, fetchUserProfile]);

  const profileId = userProfile?.userprofile_id;

  // Per-role data scope from the permission matrix (Financials module).
  // Server derives visibility from the auth token, so no scope/org param is
  // sent — we only use `financialsScope === SCOPES.GLOBAL` to gate the
  // admin-only CSV export button.
  const { scope: getScope } = usePermissions();
  const financialsScope = getScope(MODULES.FINANCIALS);

  // Top-level bar is exploratory free text — 1s debounce feels responsive
  // and matches how a "search everything" input should behave. Column
  // filters are commit-on-Apply now (see FilterInputs) — the user is
  // deliberate about opening a dropdown and clicking Apply, so we skip
  // the debounce indirection entirely there. columnFilters is the
  // canonical committed state.
  const GLOBAL_SEARCH_DEBOUNCE_MS = 1000;

  useEffect(() => {
    const t = setTimeout(() => setGlobalFilterDebounced(globalFilter.trim()), GLOBAL_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [globalFilter]);

  // Resolve the timeWindow tab into concrete (from, to) YYYY-MM-DD bounds.
  // 'all' → no bounds; 'custom' → whatever the modal resolved for the
  // chosen preset (or freeform range) into customFrom/customTo.
  const dateRange = useMemo(() => {
    if (timeWindow === 'custom') {
      return { from: customFrom || undefined, to: customTo || undefined };
    }
    return { from: undefined, to: undefined };
  }, [timeWindow, customFrom, customTo]);

  // Server-side search kicks in at 2+ chars (BE minLength).
  const searchParam =
    globalFilterDebounced.length >= 2 ? globalFilterDebounced : undefined;

  // Resolve columnSort → server (sort_by, sort_order). Non-server-sortable
  // columns keep the columnSort locally so header arrows still work, but the
  // sort is applied over just the loaded pages (documented refinement).
  const serverSortBy = SERVER_SORT_MAP[columnSort.key];
  const serverSortOrder = columnSort.dir;

  // Fold per-header column filters into server-side query params using the
  // SERVER_TEXT/NUMBER/DATE_FILTER registries. Columns that don't appear in
  // any registry (or whose filter values fall below BE minima like "2 char
  // ILIKE") get skipped — the ColumnHeader hides the filter icon for those
  // in the first place, so this branch is defensive.
  const columnFilterParams = useMemo(() => {
    const out = {};
    for (const [key, f] of Object.entries(columnFilters)) {
      if (!isFilterActive(f)) continue;
      if ((f.type === 'text' || f.type === 'airport') && SERVER_TEXT_FILTER[key]) {
        const v = String(f.value || '').trim();
        // BE requires min 2 chars for every *_search param. Airport filter
        // shares the same wire shape as text — the enriched `label` field
        // is FE-only (used for the chip display) and never sent.
        if (v.length >= 2) out[SERVER_TEXT_FILTER[key]] = v;
      } else if (f.type === 'number' && SERVER_NUMBER_FILTER[key]) {
        const [minP, maxP] = SERVER_NUMBER_FILTER[key];
        if (f.min !== '' && f.min != null) out[minP] = String(f.min);
        if (f.max !== '' && f.max != null) out[maxP] = String(f.max);
      } else if (f.type === 'date' && SERVER_DATE_FILTER[key]) {
        const [fromP, toP] = SERVER_DATE_FILTER[key];
        if (f.start) out[fromP] = f.start;
        if (f.end) out[toP] = f.end;
      } else if (f.type === 'status' && SERVER_STATUS_FILTER[key]) {
        // Two wire encodings, keyed by the mapped BE param name:
        //   SINGLE_VALUE_STATUS_PARAMS → single value (BE ILIKE
        //                substring, no multi support). UI is
        //                single-select radios; length is always ≤ 1.
        //   otherwise  → comma-separated exact-match list (e.g.
        //                status_search=SENT,ACCEPTED,COMPLETED).
        if (Array.isArray(f.values) && f.values.length) {
          const param = SERVER_STATUS_FILTER[key];
          out[param] = SINGLE_VALUE_STATUS_PARAMS.has(param)
            ? f.values[0]
            : f.values.join(',');
        }
      }
    }
    return out;
  }, [columnFilters]);

  // The one filter bag both summary and transactions share. Anything absent
  // is omitted from the request. React Query keys off the bag so any change
  // to any filter refetches both endpoints in lockstep.
  const filters = useMemo(() => {
    const f = { ...columnFilterParams };
    // Default status gating (revenue-only) when the user hasn't narrowed
    // via the Quote Status column filter. Preserves the pre-QA-revision
    // default view (Financials shows only REPORTABLE_STATUSES) without a
    // standalone Status dropdown. If the user picks statuses in the
    // column filter, columnFilterParams already wrote `f.status_search`
    // for us and this fallback is skipped — their selection wins.
    if (!f.status_search) f.status_search = REPORTABLE_STATUSES.join(',');
    // Broker filtering flows through the Broker Name column filter
    // (creator_search) via columnFilterParams — no separate created_by
    // path here anymore.
    // Filter on `sent_at` (when the quote/invoice was actually sent to the
    // client) rather than `created_at` (when the record was first inserted).
    // Matches the semantic of the "Date Quote Sent" column below and gives
    // finance a truer view of activity in the window — drafts that were
    // created but never sent are excluded.
    if (dateRange.from) f.sent_at_from = dateRange.from;
    if (dateRange.to) f.sent_at_to = dateRange.to;
    if (searchParam) f.search = searchParam;
    return f;
  }, [dateRange.from, dateRange.to, searchParam, columnFilterParams]);

  // Include chain covers every field the row mapper reads AND every field
  // the AdvanceInvoiceDrawer expects when we click into an invoice row
  // (`items` for line items, `payments` for payment history). Slim aircraft
  // per BE guidance — only tail_number + custom_profile are read for display.
  const TRANSACTIONS_INCLUDE =
    'client,creator,sender,payments,items,bid_items,bid_items.bid_options,' +
    'bid_items.bid_options.aircraft,bid_items.bid_options.aircraft.custom_profile,' +
    'bid_items.bid_options.operator';

  // KPI cards — one aggregation request per filter change.
  const { data: summary, isLoading: isSummaryLoading } = useQuery({
    queryKey: ['financial-reports-summary', filters],
    queryFn: () => apiClient.getFinancialReportsSummary(filters),
    enabled: !!profileId,
    staleTime: 60_000,
  });

  // Only include sort in the query key + request when the column is
  // server-sortable. Otherwise clicking a client-only-sortable header
  // (e.g. Trip Legs) would invalidate the cache, drop accumulated Load-
  // More pages, and fire a wasted request that comes back in BE default
  // order — client-side sort then re-orders the smaller result set.
  const sortKey = serverSortBy ? [serverSortBy, serverSortOrder] : null;

  // Status set for the TRANSACTIONS LIST only. When the user hasn't
  // narrowed via the Quote Status column filter, the list defaults to
  // REPORTABLE_STATUSES + LIST_EXTRA_STATUSES — this surfaces cancelled
  // trips on the table (ticket's audit requirement) without
  // contaminating the summary KPI query, which still uses `filters`
  // scoped to revenue statuses only. When the user picks custom
  // statuses, honor their selection verbatim — we don't force
  // CANCELLED back in.
  const listFilters = useMemo(() => {
    const f = { ...filters };
    if (!columnFilterParams.status_search) {
      f.status_search = [...REPORTABLE_STATUSES, ...LIST_EXTRA_STATUSES].join(',');
    }
    return f;
  }, [filters, columnFilterParams.status_search]);

  // Table rows — paginated. Each page returns { items, total, has_more }.
  const transactionsQuery = useInfiniteQuery({
    queryKey: ['financial-reports', listFilters, sortKey],
    queryFn: ({ pageParam = 0 }) =>
      apiClient.getFinancialReports({
        ...listFilters,
        include: TRANSACTIONS_INCLUDE,
        // Send sort params only when the column is server-sortable; omit
        // them entirely otherwise so the BE picks its default (created_at
        // desc) instead of receiving a nonsense sort_by=undefined.
        ...(serverSortBy ? { sort_by: serverSortBy, sort_order: serverSortOrder } : {}),
        skip: pageParam,
        limit: PAGE_SIZE,
      }),
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, p) => n + (p?.items?.length || 0), 0);
      if (lastPage?.has_more === false) return undefined;
      if (typeof lastPage?.total === 'number' && loaded >= lastPage.total) return undefined;
      // If BE didn't tell us has_more/total, only stop when the last page
      // returned fewer rows than we asked for.
      if (!lastPage?.items || lastPage.items.length < PAGE_SIZE) return undefined;
      return loaded;
    },
    enabled: !!profileId,
    staleTime: 30_000,
  });
  const isLoading = transactionsQuery.isLoading;
  const error = transactionsQuery.error;
  const totalMatches = transactionsQuery.data?.pages?.[0]?.total ?? null;
  const hasNextPage = transactionsQuery.hasNextPage;
  const isFetchingNextPage = transactionsQuery.isFetchingNextPage;
  // `isFetching` is TRUE any time a request is in flight, including refetches
  // after cached data is already on-screen. We use it to render a subtle
  // top-of-table progress bar without hiding the table (which would also
  // slam any open filter dropdown shut).
  const isRefetching = transactionsQuery.isFetching && !transactionsQuery.isLoading;

  // (Admin-only broker roster query removed — the standalone Broker pill
  // it fed is gone; the Broker Name column filter is a text-based
  // creator_search ILIKE and doesn't need the roster.)

  // Server row (unified quote/invoice shape) → the flat row shape the table
  // was already built around. Deleted the old mappedQuotes/mappedInvoices/
  // mappedStandaloneInvoices trio — BE does the union now.
  const mapRowToDisplay = useCallback((row) => {
    // Cancelled-trip display override. The Cancel Quote flow only
    // writes status + payment_status on the quote (no money fields,
    // no cascade into invoices), because there's no refund workflow
    // on the gtj side yet — the real charge may still be on the
    // client's card, so blindly zeroing persisted amounts would
    // misrepresent what the merchant actually settled. On Financials
    // the ticket still asks the row to read $0 across every money
    // column with status CANCELLED / payment CANCELLED, so we do
    // that at display time. Covers both the quote row itself and
    // every invoice row attached to the cancelled quote (invoice
    // rows carry the parent quote under `row.quote`).
    const parentQuoteStatus = row.row_type === 'invoice'
      ? String(row.quote?.status || '').toUpperCase()
      : String(row.quote?.status || row.status || '').toUpperCase();
    const isCancelledTrip = parentQuoteStatus === 'CANCELLED';
    const client = row.client;
    const clientName = [client?.first_name, client?.last_name].filter(Boolean).join(' ') || '-';
    // The old Broker Name column (creator-only) is now split into two
    // explicit columns — Created By (whoever authored the quote) and
    // Sent By (whoever actually delivered it to the client). These may
    // legitimately differ on delegated sends; both should show in the
    // table so audit trails read correctly.
    const formatUser = (u) => (u
      ? ([u.first_name, u.last_name].filter(Boolean).join(' ') || '-')
      : '-');
    const createdBy = formatUser(row.creator);
    const sentBy = formatUser(row.sender);

    // Trip metadata — quote rows carry a bid; invoice rows may not.
    const bid = row.bid_items?.[0]?.bid;
    const legs = bid?.leg_itinerary || [];
    const allOptions = bid?.bid_options || [];
    // Same collapse rule as QuoteCard: exactly one ACCEPTED wins post-payment.
    const primaryOption =
      allOptions.find(o => o?.status === 'ACCEPTED')
      || allOptions[0];

    const depart = legs[0]?.departure_airport || row.departure_airport;
    const arrive = legs[0]?.arrival_airport || row.arrival_airport;
    const route = depart && arrive ? `${depart} → ${arrive}` : (depart || arrive || '-');

    // Invoice rows (Advance + Additional) carry a different field shape
    // than quote rows: `subtotal` instead of `operator_cost`, a single
    // aggregate `tax_amount` instead of broken-out `total_taxes`, and
    // `preferred_method` instead of `payment_method`. The derivations
    // below map them into the columns the table reads so Operator Cost
    // / Total Taxes / Card Fees render correctly for invoice rows while
    // quote rows keep their existing behavior.
    const isAdvance = row?.invoice_type === 'ADVANCE';
    const isInvoice = isAdvance || row?.invoice_type === 'ADDITIONAL';
    const operatorCost = isInvoice
      ? (Number(row.subtotal) || 0)
      : (Number(row.operator_cost) || 0);
    const brokerMargin = Number(row.broker_margin) || 0;

    // Total Taxes — invoices carry a single aggregate; fall back to
    // summing per-item tax_amount when the aggregate is missing (older
    // records / Additional invoices with multiple services).
    const invoiceTaxTotal = (() => {
      const agg = Number(row.tax_amount);
      if (Number.isFinite(agg) && agg > 0) return agg;
      if (Array.isArray(row.items)) {
        return row.items.reduce((s, it) => s + (Number(it?.tax_amount) || 0), 0);
      }
      return 0;
    })();
    const totalTaxes = isInvoice ? invoiceTaxTotal : (Number(row.total_taxes) || 0);

    // Card Fee — prefer the stored amount (exact to the cent as billed)
    // when > 0; otherwise derive 3% of the pre-fee billable total if
    // a CC was actually captured. Rate matches the flat 3% surcharge
    // used in CardInfoForm (see commit 1e65f21).
    //
    // Base for the derivation is `subtotal + tax_amount + platform_fee`
    // — the pre-fee total the broker and client agreed to. We do NOT
    // use `total_amount` here because the capture flow updates it to
    // the fee-inclusive amount (base + fee) when the client pays by
    // card; deriving 3% of a fee-inclusive total would tax the fee
    // itself and inflate it by one cent ($22 base → $22.66 total →
    // $0.68 fee, instead of the correct $0.66). The pre-fee breakdown
    // mirrors CardInfoForm's own calculation at
    // src/features/checkout/components/CardInfoForm.jsx:65, so what
    // Financials derives always matches what the client was billed.
    //
    // CC-captured detection — strictly looks for a CAPTURED CC payment
    // record. Earlier commit was permissive (any non-FAILED CC payment
    // counted) but that fired incorrectly for "Confirm invoice without
    // taking payment from card": that flow sets invoice.status=PAID
    // and releases the hold, leaving the CC payment record at
    // status=AUTHORIZED with no capture. QA confirmed: no capture →
    // no card fee. Pre-authorized-but-not-yet-captured invoices also
    // correctly resolve to $0 here; the fee will appear once the
    // broker completes capture (which persists card_fee on the invoice).
    const CREDIT_CARD_FEE_RATE = 0.03;
    const storedCardFee = Number(row.card_fee) || 0;
    const isCardCaptured = Array.isArray(row.payments)
      && row.payments.some(
        (p) => p?.payment_method === 'CREDIT_CARD' && p?.status === 'CAPTURED',
      );
    const invoiceTotalForFee =
      (Number(row.subtotal) || 0)
      + (Number(row.tax_amount) || 0)
      + (Number(row.platform_fee) || 0);
    const cardFee = isInvoice
      ? (storedCardFee > 0
          ? storedCardFee
          : (isCardCaptured ? trunc2(invoiceTotalForFee * CREDIT_CARD_FEE_RATE) : 0))
      : storedCardFee;

    // Payment status display — CAPTURED = final "ACCEPTED" chip; anything
    // else surfaces as-is (Title-cased) so the broker sees where the
    // payment is in its lifecycle.
    const paymentStatus = row.payment_status || 'PENDING_PAYMENT';

    return {
      // Unified row id — quote_id for quote rows, invoice_id for invoice rows.
      row_id: row.row_id || row.quote_id || row.invoice_id,
      row_type: row.row_type,
      quote_id: row.quote_id || null,
      invoice_id: row.invoice_id || null,
      // Columns
      quote_number: row.quote_number || row.invoice_number || row.reference_no || '',
      client_name: clientName,
      // "Date Quote Sent" column reads sent_at (when the record was actually
      // sent to the client), not created_at. Drafts that were never sent
      // will fall back to '-' via formatDisplayDate's null-guard.
      date_quote_sent: row.sent_at,
      // Date Signed — only meaningful on quote rows (invoices don't have
      // a contract to sign). Persisted on quote.files.contract_signed_date
      // when the client submits the signed contract.
      date_signed: row.row_type === 'quote' ? (row.files?.contract_signed_date || null) : null,
      departure_date: row.departure_date, // real ISO now — no display-string hack
      route,
      trip_legs: legs.length || 0,
      // Operator Name honours the per-quote override stored in
      // `row.files.operator_name_overrides[<aircraft_id>]` so what the
      // broker edited in the pricing drawer surfaces on Financials too
      // (WYSIWYG across every screen the quote appears on — reversing
      // the earlier "keep base name in finance" default). Invoice rows
      // inherit their parent quote's `files`, so the same lookup fires
      // for them; falls back to the base operator record when no
      // override is set.
      company_name: client?.company_name || client?.company || '-',
      operator_name: getEffectiveOperatorName(
        primaryOption?.operator,
        row,
        primaryOption?.aircraft?.aircraft_id,
      ) || '-',
      tail_number: getEffectiveAircraft(primaryOption?.aircraft, row)?.tail_number || '-',
      operator_cost: isCancelledTrip ? 0 : operatorCost,
      broker_margin: isCancelledTrip ? 0 : brokerMargin,
      broker_margin_percent: isCancelledTrip
        ? 0
        : (operatorCost > 0 ? parseFloat(((brokerMargin / operatorCost) * 100).toFixed(2)) : 0),
      total_taxable_amount: isCancelledTrip ? 0 : (Number(row.taxable_amount) || 0),
      federal_excise_tax: isCancelledTrip ? 0 : (Number(row.federal_excise_tax) || 0),
      segment_tax: isCancelledTrip ? 0 : (Number(row.segment_tax) || 0),
      // International Tax — BE field not shipped yet. Read a permissive
      // set of names so whatever the BE eventually emits (numeric amount
      // or a null/blank on non-international trips) surfaces without
      // another FE change. Returns null when the value is missing so
      // the cell can render "-" like the other sparse fields. For
      // cancelled trips the display override forces 0 regardless.
      international_tax: isCancelledTrip ? 0 : (() => {
        const raw = row.international_tax ?? row.international_facilities_tax ?? null;
        if (raw === null || raw === undefined || raw === '' || raw === 'N/A') return null;
        const num = Number(raw);
        return Number.isFinite(num) ? num : null;
      })(),
      card_fee: isCancelledTrip ? 0 : cardFee,
      total_taxes: isCancelledTrip ? 0 : totalTaxes,
      total_due: isCancelledTrip ? 0 : (Number(row.total_amount ?? row.total_due) || 0),
      // Amount Paid to Broker —
      //   ADVANCE invoices: operator_cost + total_taxes (what the broker
      //     actually remits to the operator, net of platform/card fees).
      //   Everything else: total_amount − platform_fee − card_fee.
      amount_paid_to_broker: isCancelledTrip
        ? 0
        : (isAdvance
            ? operatorCost + totalTaxes
            : (operatorCost + brokerMargin + totalTaxes)),
      date_paid: row.paid_at || '',
      created_by: createdBy,
      sent_by: sentBy,
      // Quote Status source depends on the row type:
      //   quote row    → row.quote.status  (the quote's own lifecycle field)
      //   invoice row  → row.status        (the invoice's own status)
      // Cancelled-trip override: whenever the parent quote is
      // CANCELLED, override both the invoice-side status column and
      // the payment column so every row for that trip reads
      // CANCELLED consistently, regardless of what the invoice's own
      // status still says.
      quote_status: isCancelledTrip
        ? 'CANCELLED'
        : (row.row_type === 'quote'
            ? (row.quote?.status || row.status || '-')
            : (row.status || '-')),
      payment_status: isCancelledTrip ? 'CANCELLED' : paymentStatus,
      // Keep the raw payload around so the invoice drawer can still open
      // with the same object shape it expected from the old fetch path.
      _row: row,
    };
  }, []);

  // Flatten paginated pages, then map each server row to display shape once.
  const flatRows = useMemo(() => {
    const pages = transactionsQuery.data?.pages || [];
    const items = pages.flatMap(p => p?.items || []);
    return items.map(mapRowToDisplay);
  }, [transactionsQuery.data, mapRowToDisplay]);

  // Every per-header filter now flows into server-side params via
  // columnFilterParams, so the loaded pages are already the filtered set.
  // On top of that we drop rows that shouldn't surface on Financials at all:
  //   • Quotes hide until they've reached a monetary state — anything still
  //     in the pre-payment funnel (DRAFT/SENT/VIEWED) or a terminal
  //     non-earning state (EXPIRED/DECLINED/CANCELLED) is noise here.
  //   • Invoices only ever surface once they're PAID or actively SENT.
  // Keeping the sortedData → filteredData → flatRows chain intact.
  const filteredData = useMemo(() => {
    // CANCELLED is intentionally NOT hidden here — the Cancel Quote
    // flow on confirmed trips (QuoteDetailsDrawer.handleCancelConfirmedTrip)
    // zeros out every money field so the row stays in Financials at
    // $0 for audit/reference, per the ticket's "retain the transaction
    // record" requirement.
    const HIDDEN_QUOTE_STATUSES = new Set([
      'DRAFT', 'SENT', 'VIEWED', 'EXPIRED', 'DECLINED',
    ]);
    const VISIBLE_INVOICE_STATUSES = new Set(['PAID', 'SENT']);
    return flatRows.filter(r => {
      const status = String(r.quote_status || '').toUpperCase();
      if (r.row_type === 'invoice') return VISIBLE_INVOICE_STATUSES.has(status);
      return !HIDDEN_QUOTE_STATUSES.has(status);
    });
  }, [flatRows]);

  const sortedData = useMemo(() => {
    const arr = [...filteredData];
    if (!columnSort?.key) return arr;
    const col = COLUMNS.find(c => c.key === columnSort.key);
    if (!col) return arr;
    const sign = columnSort.dir === 'desc' ? -1 : 1;
    arr.sort((a, b) => {
      const av = a[columnSort.key];
      const bv = b[columnSort.key];
      if (col.type === 'number') return ((Number(av) || 0) - (Number(bv) || 0)) * sign;
      if (col.type === 'date') {
        const ad = new Date(av || 0).getTime() || 0;
        const bd = new Date(bv || 0).getTime() || 0;
        return (ad - bd) * sign;
      }
      return String(av || '').localeCompare(String(bv || '')) * sign;
    });
    return arr;
  }, [filteredData, columnSort]);

  const handleColumnSort = (key) => {
    setColumnSort(prev => {
      if (prev?.key === key) return { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' };
      const col = COLUMNS.find(c => c.key === key);
      // Numbers and dates default to descending (most-recent / largest first),
      // text defaults to ascending (A–Z).
      const defaultDir = col?.type === 'text' ? 'asc' : 'desc';
      return { key, dir: defaultDir };
    });
  };

  const setColumnFilter = (key, value) => {
    setColumnFilters(prev => {
      const next = { ...prev };
      if (value == null) delete next[key]; else next[key] = value;
      return next;
    });
  };

  const toggleRowSelection = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const visibleSelectedCount = useMemo(
    () => sortedData.reduce((n, q) => n + (selectedIds.has(q.row_id) ? 1 : 0), 0),
    [sortedData, selectedIds],
  );
  const allVisibleSelected = sortedData.length > 0 && visibleSelectedCount === sortedData.length;
  const someVisibleSelected = visibleSelectedCount > 0 && !allVisibleSelected;

  const toggleSelectAllVisible = () => {
    setSelectedIds(prev => {
      if (allVisibleSelected) {
        // Deselect only the currently visible rows; preserve selections that
        // fell outside the current filter window (survives across Load More).
        const next = new Set(prev);
        sortedData.forEach(q => next.delete(q.row_id));
        return next;
      }
      const next = new Set(prev);
      sortedData.forEach(q => next.add(q.row_id));
      return next;
    });
  };

  // Ref on the outer wrapper around the Table — used by the fixed-header
  // effect below to locate the real thead + its scroll container.
  const outerScrollRef = useRef(null);

  // Fixed-position header clone — appears at viewport top once the real
  // header has scrolled above the fold. See the effect + JSX below for
  // the full explanation of why we can't use position: sticky here.
  const [isHeaderFixed, setIsHeaderFixed] = useState(false);
  const [fixedHeaderLeft, setFixedHeaderLeft] = useState(0);
  const [fixedHeaderWidth, setFixedHeaderWidth] = useState(0);
  const [columnWidths, setColumnWidths] = useState([]);
  const [fixedScrollLeft, setFixedScrollLeft] = useState(0);

  // Fixed-header effect — see the fixed clone JSX below.
  //
  // Why not `position: sticky`? Sticky needs a scrolling ancestor to attach
  // to. The table's horizontal scroll wrapper is `overflow-x: auto`, which
  // per spec forces `overflow-y: auto` too; that creates a nested scroll
  // context that traps sticky. So sticky either sticks to the wrapper's
  // top (which itself scrolls off with the page) or, if we window the
  // wrapper with max-height, we get a duplicate scrollbar. Neither is OK.
  //
  // Instead we render a duplicate `<thead>` inside a `position: fixed`
  // clone at the top of the viewport, keyed by scroll listeners that
  // update from getBoundingClientRect reads.
  //
  // Scroll source: AppLayout's <main> has overflow-auto, so page scroll
  // happens on <main>, NOT on window. Element scroll events don't bubble,
  // so a plain window listener misses them. We attach on document with
  // capture:true, which fires for scroll on any descendant during the
  // capture phase — catches <main>, catches window, catches anything
  // scrollable between us and the root.
  useEffect(() => {
    const outer = outerScrollRef.current;
    if (!outer) return undefined;
    const tableContainer = outer.querySelector('[data-slot="table-container"]');
    const thead = outer.querySelector('[data-slot="table-header"]');
    if (!tableContainer || !thead) return undefined;

    let rafPending = false;

    const measure = () => {
      const ths = Array.from(thead.querySelectorAll('tr:first-child > th'));
      setColumnWidths(ths.map(th => th.getBoundingClientRect().width));
      const rect = tableContainer.getBoundingClientRect();
      setFixedHeaderLeft(rect.left);
      setFixedHeaderWidth(rect.width);
      setFixedScrollLeft(tableContainer.scrollLeft);
    };

    const checkFixed = () => {
      rafPending = false;
      const rect = thead.getBoundingClientRect();
      const containerRect = tableContainer.getBoundingClientRect();
      // Show the clone once the real header has scrolled above the
      // viewport top AND the table body is still visible in the
      // viewport (hide once the whole table has scrolled off).
      const stuck = rect.top < 0 && containerRect.bottom > 0;
      setIsHeaderFixed(stuck);
      // Refresh horizontal offset — the container's left edge shifts
      // on horizontal scroll or sidebar collapse.
      setFixedHeaderLeft(containerRect.left);
      setFixedHeaderWidth(containerRect.width);
    };

    const onScroll = () => {
      if (rafPending) return;
      rafPending = true;
      requestAnimationFrame(checkFixed);
    };

    // Initial measurement + fixed-state check on next frame so the DOM
    // has settled after the current render/paint.
    measure();
    requestAnimationFrame(checkFixed);

    const ro = new ResizeObserver(() => {
      measure();
      checkFixed();
    });
    ro.observe(tableContainer);
    const table = tableContainer.querySelector('table');
    if (table) ro.observe(table);

    const onHorizontalScroll = () => setFixedScrollLeft(tableContainer.scrollLeft);
    tableContainer.addEventListener('scroll', onHorizontalScroll, { passive: true });

    const onResize = () => { measure(); checkFixed(); };

    // capture:true is the key — scroll events don't bubble, so a plain
    // listener on document (or window) misses scroll on inner elements
    // like <main>. Capture phase fires before the event reaches its
    // target, so we get every scroll everywhere.
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    window.addEventListener('resize', onResize);

    return () => {
      ro.disconnect();
      tableContainer.removeEventListener('scroll', onHorizontalScroll);
      document.removeEventListener('scroll', onScroll, { capture: true });
      window.removeEventListener('resize', onResize);
    };
  }, [sortedData.length, groupsExpanded]);

  // KPIs live on `summary` from the summary endpoint now — the old
  // client-side reduce is gone. The four cards read directly from
  // `summary.total_revenue`, `summary.total_commissions`, `summary.top_client`,
  // and `summary.quote_to_closed_ratio`, all filter-scoped server-side.
  const topClientName = summary?.top_client
    ? [summary.top_client.first_name, summary.top_client.last_name].filter(Boolean).join(' ') || '-'
    : '-';

  // ─── Active-filters chip row ───────────────────────────────────────────
  // One row of removable pills mirroring every filter currently applied
  // (broker + status + date + global search + every column filter).
  // Renders below the toolbar. Clicking a chip removes just that filter;
  // "Clear all" resets every filter at once. Hidden when nothing's applied.

  const handleClearAll = useCallback(() => {
    setTimeWindow('all');
    setCustomFrom('');
    setCustomTo('');
    setModalPresetId('all');
    setGlobalFilter('');
    setColumnFilters({});
  }, []);

  const activeChips = useMemo(() => {
    const chips = [];

    // (Standalone Broker chip removed — Broker Name filter now emits
    // its chip through the generic columnFilters loop below.)

    // Quote/Payment status chips are emitted from the columnFilters loop
    // below (`type === 'status'` case in the column-filter dispatch) — the
    // status filter now lives on the column header, not as a standalone
    // toolbar dropdown.

    if (timeWindow !== 'all') {
      const dateLabel =
        PRESET_LABELS[modalPresetId]
        || (timeWindow === 'custom' ? `${customFrom || '…'} → ${customTo || '…'}` : '');
      chips.push({
        id: 'date',
        label: `Date: ${dateLabel}`,
        onClear: () => {
          setTimeWindow('all');
          setCustomFrom('');
          setCustomTo('');
          setModalPresetId('all');
        },
      });
    }

    if (globalFilter.trim().length >= 2) {
      chips.push({
        id: 'search',
        label: `Search: "${globalFilter.trim()}"`,
        onClear: () => setGlobalFilter(''),
      });
    }

    // Column filters — one chip per active per-header filter. Label format
    // depends on filter type; label uses the column's display label so the
    // chip echoes the header the user sees.
    for (const [key, f] of Object.entries(columnFilters)) {
      if (!isFilterActive(f)) continue;
      const col = COLUMNS.find(c => c.key === key);
      if (!col) continue;
      let value = '';
      if (f.type === 'text') {
        value = `"${f.value}"`;
      } else if (f.type === 'airport') {
        // Show the airport name inline when the user picked a resolved
        // suggestion (label was populated by AirportFilterInput). If they
        // just typed a partial code and hit apply, show the value alone.
        value = f.label ? `${f.value} (${f.label})` : f.value;
      } else if (f.type === 'number') {
        const hasMin = f.min !== '' && f.min != null;
        const hasMax = f.max !== '' && f.max != null;
        if (hasMin && hasMax) value = `${f.min}–${f.max}`;
        else if (hasMin) value = `≥ ${f.min}`;
        else if (hasMax) value = `≤ ${f.max}`;
      } else if (f.type === 'date') {
        value = `${f.start || '…'} → ${f.end || '…'}`;
      } else if (f.type === 'status') {
        // Show up to 3 human-readable statuses inline; anything beyond
        // becomes a "+N" suffix so the chip stays compact when the user
        // has picked several.
        const vals = Array.isArray(f.values) ? f.values : [];
        value = vals.length <= 3
          ? vals.join(', ').replace(/_/g, ' ')
          : `${vals.slice(0, 2).join(', ').replace(/_/g, ' ')} +${vals.length - 2}`;
      }
      chips.push({
        id: `col:${key}`,
        label: `${col.label}: ${value}`,
        onClear: () => setColumnFilter(key, null),
      });
    }

    return chips;
  }, [
    timeWindow, customFrom, customTo, modalPresetId,
    globalFilter,
    columnFilters,
  ]);

  const formatDisplayDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
    const year = date.getFullYear().toString().slice(-2);
    return `${day}-${month}-${year}`;
  };

  const buildCsvBlob = (exportRows) => {
    const csvText = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

    // date_quote_sent / date_paid are typed as 'text' in COLUMNS but hold ISO
    // dates — render them like the table does.
    const DATE_KEYS = new Set(['departure_date', 'date_quote_sent', 'date_signed', 'date_paid']);
    const SUMMABLE_NUMBER_KEYS = COLUMNS
      .filter(c => c.type === 'number' && c.key !== 'broker_margin_percent')
      .map(c => c.key);

    const formatCell = (q, col) => {
      const v = q[col.key];
      if (DATE_KEYS.has(col.key)) return csvText(v ? formatDisplayDate(v) : '');
      if (col.key === 'broker_margin_percent') return csvText(`${(Number(v) || 0).toFixed(2)}%`);
      if (col.type === 'number') return (Number(v) || 0).toFixed(2);
      return csvText(v);
    };

    const headers = COLUMNS.map(c => csvText(c.label));
    const rows = exportRows.map(q => COLUMNS.map(c => formatCell(q, c)));

    // Totals reflect exactly what was exported (selected subset or full visible).
    const totalsByKey = SUMMABLE_NUMBER_KEYS.reduce((acc, key) => {
      acc[key] = exportRows.reduce((s, q) => s + (Number(q[key]) || 0), 0);
      return acc;
    }, {});
    const totalsRow = COLUMNS.map((col, i) => {
      if (i === 0) return csvText('TOTALS');
      if (!(col.key in totalsByKey)) return csvText('');
      return totalsByKey[col.key].toFixed(2);
    });
    rows.push(totalsRow);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    return new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  };

  const triggerCsvDownload = (blob) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `tax-report-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Revoke on the next tick so the download has a chance to start.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const handleDownloadCSV = async () => {
    // If the user has explicitly checked rows, export just those (from the
    // already-loaded pages). Otherwise fetch the FULL filtered set from the
    // server (no skip/limit) so the export covers every matching row across
    // all pages, not just what's on-screen.
    let exportRows;
    if (selectedIds.size > 0) {
      exportRows = sortedData.filter(q => selectedIds.has(q.row_id));
    } else {
      try {
        setIsExporting(true);
        const { items } = await apiClient.getFinancialReports({
          ...filters,
          include: TRANSACTIONS_INCLUDE,
          sort_by: serverSortBy,
          sort_order: serverSortOrder,
        });
        exportRows = (items || []).map(mapRowToDisplay);
      } catch (e) {
        console.error('CSV export failed', e);
        return;
      } finally {
        setIsExporting(false);
      }
    }
    if (!exportRows.length) return;
    triggerCsvDownload(buildCsvBlob(exportRows));
  };

  return (
    <div className="pb-20 bg-white/50 backdrop-blur-sm rounded-[2rem] p-4 md:p-6">
      {/* KPI cards — driven by /financial/reports/summary. Server aggregates
          revenue + commissions over paid rows only. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-2">
        <KpiCard
          label="Total Revenue"
          value={isSummaryLoading ? '…' : compactCurrency(Number(summary?.total_revenue) || 0)}
          Icon={RiMoneyDollarCircleLine}
          iconBg="bg-blue-50"
          iconColor="text-blue-600"
        />
        <KpiCard
          label="Total Commissions"
          value={isSummaryLoading ? '…' : compactCurrency(Number(summary?.total_commissions) || 0)}
          Icon={RiLineChartLine}
          iconBg="bg-green-100"
          iconColor="text-green-500"
        />
        <KpiCard
          label="Top Client"
          value={isSummaryLoading ? '…' : topClientName}
          subtitle="Highest booking volume"
          valueSize="text-lg"
          Icon={RiTeamLine}
          iconBg="bg-amber-50"
          iconColor="text-amber-600"
        />
        <KpiCard
          label="Quote-to-Closed Trip Ratio"
          value={isSummaryLoading ? '…' : `${summary?.quote_to_closed_ratio ?? 0}%`}
          subtitle={
            isSummaryLoading
              ? ''
              : `${summary?.quotes_closed ?? 0} closed trips / ${summary?.quotes_sent ?? 0} quotes sent`
          }
          Icon={RiPercentLine}
          iconBg="bg-orange-50"
          iconColor="text-orange-600"
        />
      </div>

      {/* Toolbar */}
      <Card className="mb-2 !bg-transparent !border-0 !shadow-none">
        <CardContent className="p-4 !px-0">
          <div className="flex flex-wrap items-center gap-3">
            <Tabs
              value={timeWindow}
              onValueChange={(v) => {
                if (v === 'custom') {
                  // The Custom tab always opens the modal — the tab's active
                  // state doesn't flip until the user actually applies a
                  // range (see modal's onApply below).
                  setIsCustomDateOpen(true);
                  return;
                }
                // Only remaining explicit click target is 'all' — reset
                // both the applied range and the modal's next-open preset.
                setModalPresetId('all');
                setTimeWindow('all');
                setCustomFrom('');
                setCustomTo('');
              }}
              className="!bg-gray-100 !rounded-lg"
            >
              <TabsList className="!h-auto gap-2">
                <TabsTrigger value="all" className="!px-[30px] text-gray-400 !rounded-lg data-[state=active]:!bg-white data-[state=active]:text-gray-700">All</TabsTrigger>
                <TabsTrigger
                  value="custom"
                  // Radix Tabs won't fire onValueChange when the current tab
                  // is re-clicked, so a bare `value="custom"` handler misses
                  // the "I want to change my range" click once Custom is
                  // already active. Explicit onClick ensures the modal
                  // opens every time the trigger is pressed.
                  onClick={() => setIsCustomDateOpen(true)}
                  className="!px-[20px] text-gray-400 !rounded-lg data-[state=active]:!bg-white data-[state=active]:text-gray-700"
                >
                  {timeWindow === 'custom'
                    ? (PRESET_LABELS[modalPresetId]
                        || ((customFrom || customTo) ? `${customFrom || '…'} → ${customTo || '…'}` : 'Custom'))
                    : 'Custom'}
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Dedicated search bar — writes to `globalFilter`, hits the BE
                `search=` param after a 400ms debounce (min 2 chars). Fills
                the previously-empty spacer between the date tabs and the
                filter/action pills. Placeholder mirrors what's actually
                searchable so users don't type "$3,500" and hit an empty
                result. */}
            <div className="flex-1 min-w-[200px] max-w-md relative">
              <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
                placeholder="Search trip #, client, broker, airport, operator, tail…"
                className="w-full pl-9 pr-9 py-2 bg-white border border-gray-200 rounded-full text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              {globalFilter && (
                <button
                  type="button"
                  onClick={() => setGlobalFilter('')}
                  aria-label="Clear search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                >
                  <RiCloseLine className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Standalone Broker filter pill removed per QA revision —
                the Broker Name column filter (creator_search ILIKE) is
                the single source of broker filtering now. The column's
                cell displays creator name only, so what users see is
                what the filter matches. */}

            {/* Status filter and Sort By dropdown removed per QA
                revision — status now lives inside the Quote Status /
                Payment Status column filters, and sorting is done via
                the per-column header sort. */}

            {financialsScope === SCOPES.GLOBAL && (
              <button
                onClick={handleDownloadCSV}
                disabled={isExporting || (!sortedData.length && selectedIds.size === 0)}
                className="px-4 py-2 bg-primary-500 text-white rounded-full text-sm hover:bg-green-600 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isExporting
                  ? <RiLoader4Line className="w-4 h-4 animate-spin" />
                  : <RiDownloadLine className="w-4 h-4" />}
                {isExporting
                  ? 'Preparing…'
                  : (selectedIds.size > 0 ? `Export ${selectedIds.size} Selected` : 'Export to CSV')}
              </button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Active-filters chip row. Mirrors every applied filter (broker,
          status, date, global search, per-header column filters) as a
          removable pill so the user has a single source of truth for
          "what's currently narrowing this view." Hidden when nothing's
          applied so we don't burn vertical space on an empty state. */}
      {activeChips.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-gray-500 mr-1">
            Active filters:
          </span>
          {activeChips.map((chip) => (
            <button
              key={chip.id}
              type="button"
              onClick={chip.onClear}
              title="Remove this filter"
              className="group inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs bg-gray-100 text-gray-700 hover:bg-gray-200 border border-transparent hover:border-gray-300 transition-colors max-w-[260px]"
            >
              <span className="truncate">{chip.label}</span>
              <RiCloseLine className="w-3.5 h-3.5 shrink-0 text-gray-400 group-hover:text-gray-600" />
            </button>
          ))}
          {activeChips.length >= 2 && (
            <button
              type="button"
              onClick={handleClearAll}
              className="text-xs text-primary-600 hover:text-primary-700 hover:underline ml-1"
            >
              Clear all
            </button>
          )}
        </div>
      )}

      <Card className="overflow-hidden">
        <CardContent className="p-0">
          {/* Thin top indicator while a refetch is in flight over already-
              displayed data. Keeps the table + header + any open filter
              dropdown visible so the user can keep interacting. */}
          {isRefetching && (
            <div className="h-0.5 w-full bg-primary-100 overflow-hidden">
              <div className="h-full w-1/3 bg-primary-500 animate-pulse" />
            </div>
          )}
          {(() => {
            // Rebuild the header row once per render so the real table
            // and the fixed clone below stay in perfect sync (same JSX,
            // same handlers, same state reads). Extracted as an IIFE
            // instead of a memo because it depends on ~half a dozen
            // pieces of state and re-runs cheaply.
            const ANCHOR_TO_GROUP = {
              client_name: 'client',
              route: 'flight',
              total_due: 'cost',
              payment_status: 'tracking',
            };
            const groupCols = (groupKey) => COLUMNS.filter(c => c.category === groupKey);
            const renderColHead = (col) => {
              const isGroupCol = col.category !== 'always';
              return (
                <TableHead
                  key={col.key}
                  className={`!text-gray-600 !font-normal ${isGroupCol ? '!bg-[#EFEFEF]' : ''} ${col.align === 'right' ? 'text-right' : ''}`}
                >
                  <ColumnHeader
                    col={col}
                    sort={columnSort}
                    onSort={handleColumnSort}
                    filter={columnFilters[col.key]}
                    onFilterChange={(val) => setColumnFilter(col.key, val)}
                    serverFilterable={isColumnServerFilterable(col.key)}
                    serverSortable={isColumnServerSortable(col.key)}
                  />
                </TableHead>
              );
            };
            const anchoredHeads = [];
            alwaysCols.forEach(col => {
              const anchoredGroup = ANCHOR_TO_GROUP[col.key];
              if (anchoredGroup) {
                const isExpanded = !!groupsExpanded[anchoredGroup];
                const groupLabel = COLUMN_GROUPS.find(g => g.key === anchoredGroup)?.label || '';
                anchoredHeads.push(
                  <TableHead key={col.key} className={`!text-gray-600 !font-normal ${col.align === 'right' ? 'text-right' : ''}`}>
                    <div className={`flex items-center gap-2 ${col.align === 'right' ? 'justify-end' : ''}`}>
                      <ColumnHeader
                        col={col}
                        sort={columnSort}
                        onSort={handleColumnSort}
                        filter={columnFilters[col.key]}
                        onFilterChange={(val) => setColumnFilter(col.key, val)}
                        serverFilterable={isColumnServerFilterable(col.key)}
                        serverSortable={isColumnServerSortable(col.key)}
                      />
                      <GroupToggleButton
                        expanded={isExpanded}
                        groupLabel={groupLabel}
                        onToggle={() => toggleGroup(anchoredGroup)}
                      />
                    </div>
                  </TableHead>
                );
                if (isExpanded) {
                  groupCols(anchoredGroup).forEach(gcol => anchoredHeads.push(renderColHead(gcol)));
                }
              } else {
                anchoredHeads.push(renderColHead(col));
              }
            });

            const headerRowContent = (
              <TableRow className="bg-gray-100">
                <TableHead className="w-10">
                  <Checkbox
                    checked={allVisibleSelected ? true : (someVisibleSelected ? 'indeterminate' : false)}
                    onCheckedChange={toggleSelectAllVisible}
                    aria-label="Select all visible rows"
                    className="!bg-white"
                  />
                </TableHead>
                {anchoredHeads}
              </TableRow>
            );

            return (
              <>
                <div ref={outerScrollRef}>
                    <Table>
                      <TableHeader>
                        {/* Single header row — no separate group-title
                            band. Each of the three collapsible groups is
                            opened by an inline GroupToggleButton sitting
                            beside its anchor column:
                              Route          → Flight Details
                              Total Amount   → Cost Breakdown
                              Payment Status → Quote Tracking
                            When a group is expanded its columns are
                            inserted IMMEDIATELY after their anchor. */}
                        {headerRowContent}
                      </TableHeader>
                      <TableBody>
                  {/* First-load skeleton — header (above) stays visible so
                      the user knows which columns are on their way. */}
                  {isLoading && Array.from({ length: 8 }).map((_, i) => (
                    <TableRow key={`skel-${i}`}>
                      <TableCell colSpan={totalCellCount} className="py-3">
                        <Skeleton className="h-5 w-full" />
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Error state — surface it in a full-width row instead
                      of unmounting the header, so filter dropdowns stay
                      accessible for the user to retry. */}
                  {!isLoading && error && (
                    <TableRow>
                      <TableCell colSpan={totalCellCount} className="h-48 p-0 border-0">
                        <div className="sticky left-0 h-full flex flex-col items-center justify-center gap-2 text-gray-400" style={{ width: '100vw' }}>
                          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-1">
                            <RiFileList3Line className="w-8 h-8 text-red-400" />
                          </div>
                          <p className="text-base font-semibold text-gray-900">Couldn't load tax data</p>
                          <p className="text-sm text-gray-500">{error.message || 'Please try again.'}</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  {!isLoading && !error && sortedData.map((quote) => (
                    <TableRow key={quote.row_id} data-state={selectedIds.has(quote.row_id) ? 'selected' : undefined}>
                      <TableCell className="w-10">
                        <Checkbox
                          checked={selectedIds.has(quote.row_id)}
                          onCheckedChange={() => toggleRowSelection(quote.row_id)}
                          aria-label={`Select ${quote.quote_number}`}
                        />
                      </TableCell>
                      <TableCell className="font-medium">
                        <button
                          type="button"
                          onClick={() => {
                            // Invoice-typed rows open in the AdvanceInvoiceDrawer;
                            // quote-typed rows open the QuoteDetailsDrawer. row_type
                            // is authoritative — we no longer sniff a client-side
                            // `_invoice` marker.
                            if (quote.row_type === 'invoice') {
                              setInvoiceDrawerData(quote._row);
                              setInvoiceDrawerId(quote.invoice_id);
                              setInvoiceDrawerStatus(quote.payment_status);
                              setIsInvoiceDrawerOpen(true);
                            } else {
                              setDrawerQuoteId(quote.quote_id);
                              setIsDrawerOpen(true);
                            }
                          }}
                          className="text-primary-600 hover:text-primary-700 hover:underline focus:outline-none"
                        >
                          {quote.quote_number}
                        </button>
                      </TableCell>
                      {/* Trip ID button lives in the preceding TableCell
                          block. Order below matches the header exactly:
                          Client / Next Flight Date / Route → (Flight
                          Details cols) → Total Amount → (Cost Breakdown
                          cols) → Payment Status → (Quote Tracking
                          cols). */}
                      <TableCell>{quote.client_name}</TableCell>

                      {/* Client Details — inserted right after Client Name (its anchor). */}
                      {groupsExpanded.client && (
                        <>
                          <TableCell className="!bg-[#F6F6F6]">{quote.company_name}</TableCell>
                        </>
                      )}

                      <TableCell>{formatDisplayDate(quote.departure_date)}</TableCell>
                      <TableCell>{quote.route}</TableCell>

                      {/* Flight Details — inserted right after Route
                          (its anchor column). Cells share the group
                          tint so the stripe of expanded columns reads
                          as one unit. No spacer cell when collapsed;
                          the header row skips these too. */}
                      {groupsExpanded.flight && (
                        <>
                          <TableCell className="!bg-[#F6F6F6]">{quote.operator_name}</TableCell>
                          <TableCell className="!bg-[#F6F6F6]">{quote.tail_number}</TableCell>
                          <TableCell className="!bg-[#F6F6F6]">{quote.trip_legs}</TableCell>
                        </>
                      )}

                      <TableCell>${quote.total_due.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 10 })}</TableCell>

                      {/* Cost Breakdown — inserted right after Total Amount. */}
                      {groupsExpanded.cost && (
                        <>
                          <TableCell className="!bg-[#F6F6F6]">{formatPrice(quote.operator_cost)}</TableCell>
                          <TableCell className="!bg-[#F6F6F6]">{formatPrice(quote.broker_margin)}</TableCell>
                          <TableCell className="!bg-[#F6F6F6]">{Number(quote.broker_margin_percent).toFixed(2)}%</TableCell>
                          <TableCell className="!bg-[#F6F6F6]">{formatPrice(quote.total_taxable_amount)}</TableCell>
                          <TableCell className="!bg-[#F6F6F6]">{formatPrice(quote.federal_excise_tax)}</TableCell>
                          <TableCell className="!bg-[#F6F6F6]">{formatPrice(quote.segment_tax)}</TableCell>
                          <TableCell className="!bg-[#F6F6F6]">
                            {quote.international_tax == null ? '-' : formatPrice(quote.international_tax)}
                          </TableCell>
                          <TableCell className="!bg-[#F6F6F6]">{formatPrice(quote.total_taxes)}</TableCell>
                          <TableCell className="!bg-[#F6F6F6]">{formatPrice(quote.card_fee)}</TableCell>
                          <TableCell className="!bg-[#F6F6F6]">{formatPrice(quote.amount_paid_to_broker)}</TableCell>
                        </>
                      )}

                      <TableCell>
                        {quote.payment_status === '-' ? (
                          '-'
                        ) : (() => {
                          const key = String(quote.payment_status || '').toUpperCase().replace(/\s+/g, '_');
                          const config = getQuoteStatusConfig(key);
                          const Icon = config.icon;
                          return (
                            <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md ${config.className}`}>
                              {Icon && <Icon className="w-3.5 h-3.5" />}
                              {config.label}
                            </span>
                          );
                        })()}
                      </TableCell>

                      {/* Quote Tracking — inserted right after Payment Status. */}
                      {groupsExpanded.tracking && (
                        <>
                          <TableCell className="!bg-[#F6F6F6]">
                            {(() => {
                              // Apply the PAID → ACCEPTED display hack first,
                              // then run through the promotion helper so
                              // PASSENGERS_PENDING reads as "Trip Sheet In
                              // Progress" once the client has submitted
                              // passenger details.
                              const rawStatus = quote.quote_status === 'PAID' ? 'ACCEPTED' : quote.quote_status;
                              const config = getQuoteStatusConfig(getEffectiveQuoteStatus(quote._row, rawStatus));
                              const Icon = config.icon;
                              return quote.quote_status!="-" ? (
                                <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md ${config.className}`}>
                                  {Icon && <Icon className="w-3.5 h-3.5" />}
                                  {config.label}
                                </span>
                              ) : " - ";
                            })()}
                          </TableCell>
                          <TableCell className="!bg-[#F6F6F6]">{formatDisplayDate(quote.date_quote_sent)}</TableCell>
                          <TableCell className="!bg-[#F6F6F6]">{quote.date_signed ? formatDisplayDate(quote.date_signed) : '-'}</TableCell>
                          <TableCell className="!bg-[#F6F6F6]">{quote.date_paid ? formatDisplayDate(quote.date_paid) : '-'}</TableCell>
                          <TableCell className="!bg-[#F6F6F6]">{quote.created_by}</TableCell>
                          <TableCell className="!bg-[#F6F6F6]">{quote.sent_by}</TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                  {/* <TableRow className="bg-gray-100 font-semibold">
                    <TableCell colSpan={7}>Totals</TableCell>
                    <TableCell>{totals.totalSegments}</TableCell>
                    <TableCell colSpan={3}></TableCell>
                    <TableCell>{formatPrice(totals.totalOperatorCost)}</TableCell>
                    <TableCell>{formatPrice(totals.totalBrokerMargin)}</TableCell>
                    <TableCell>{formatPrice(totals.totalTaxable)}</TableCell>
                    <TableCell>{formatPrice(totals.totalFET)}</TableCell>
                    <TableCell>{formatPrice(totals.totalSegmentFee)}</TableCell>
                    <TableCell>{formatPrice(totals.totalCardFee)}</TableCell>
                    <TableCell>{formatPrice(totals.totalTaxes)}</TableCell>
                    <TableCell>{formatPrice(totals.totalDue)}</TableCell>
                    <TableCell colSpan={6}></TableCell>
                  </TableRow> */}
                  {!isLoading && !error && sortedData?.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={totalCellCount} className="h-48 p-0 border-0">
                        <div className="sticky left-0 h-full flex flex-col items-center justify-center gap-2 text-gray-400" style={{ width: '100vw' }}>
                          <RiFileList3Line className="w-10 h-10" />
                          <p className="text-sm font-medium text-gray-500">No results match your filters</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              {/* Load More — server-paged. `total` comes from page[0]; if the
                  BE ever stops returning it, the button still works and just
                  hides once a short page comes back (see getNextPageParam).
                  Hidden during initial load — the skeleton rows above stand
                  in until the first page comes back. */}
              {!isLoading && !error && sortedData.length > 0 && (
                <div className="sticky left-0 flex items-center justify-between gap-3 px-4 py-3 border-t border-gray-100 bg-white text-xs text-gray-500">
                  <span>
                    {totalMatches != null
                      ? `Showing ${sortedData.length.toLocaleString()} of ${totalMatches.toLocaleString()}`
                      : `Showing ${sortedData.length.toLocaleString()}`}
                  </span>
                  {hasNextPage && (
                    <button
                      type="button"
                      onClick={() => transactionsQuery.fetchNextPage()}
                      disabled={isFetchingNextPage}
                      className="px-4 py-1.5 text-sm rounded-full border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                    >
                      {isFetchingNextPage && <RiLoader4Line className="w-3.5 h-3.5 animate-spin" />}
                      {isFetchingNextPage ? 'Loading…' : 'Load more'}
                    </button>
                  )}
                </div>
              )}
            </div>
            {/* Fixed-position header clone — see the useEffect above.
                Portaled to document.body so its position:fixed can never
                be hijacked by a transformed / filtered / contained
                ancestor (a common gotcha that silently reparents
                position:fixed to that ancestor and makes it look like
                sticky isn't working). Column widths come from the
                measured real thead via <colgroup>; the inner translateX
                mirrors the table's scrollLeft so the clone tracks
                horizontal scroll pixel-for-pixel. Sort/filter/checkbox
                handlers stay live because the elements are the same
                React nodes as the real header — same state. */}
            {isHeaderFixed && columnWidths.length > 0 && createPortal(
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: fixedHeaderLeft,
                  width: fixedHeaderWidth,
                  overflow: 'hidden',
                  zIndex: 50,
                  background: '#F3F4F6', // Tailwind gray-100 — matches real header
                  boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                }}
              >
                <div style={{ transform: `translateX(-${fixedScrollLeft}px)`, width: 'max-content' }}>
                  <table
                    className="text-sm"
                    style={{ tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: 0 }}
                  >
                    <colgroup>
                      {columnWidths.map((w, i) => (
                        <col key={i} style={{ width: `${w}px` }} />
                      ))}
                    </colgroup>
                    <thead>
                      {headerRowContent}
                    </thead>
                  </table>
                </div>
              </div>,
              document.body,
            )}
              </>
            );
          })()}
        </CardContent>
      </Card>

      <QuoteDetailsDrawer
        isOpen={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
        quoteId={drawerQuoteId}
      />

      <AdvanceInvoiceDrawer
        isOpen={isInvoiceDrawerOpen}
        onClose={() => setIsInvoiceDrawerOpen(false)}
        invoice={invoiceDrawerData}
        invoiceId={invoiceDrawerId}
        status={invoiceDrawerStatus}
      />

      {/* Custom Date Range modal — dual-month calendar + preset rail.
          onApply returns a presetId + resolved range. All time collapses
          into the All tab; every other preset (Last 30 days, Month to
          date, Year to date, Last 7 / Last 3 mo / Last 12 mo, or a
          freeform pick) resolves to concrete from/to dates and lands on
          the Custom tab. QA note: Monthly / Yearly used to be toolbar
          tabs but were removed as redundant with these presets. */}
      <FinancialsDateRangeModal
        isOpen={isCustomDateOpen}
        onClose={() => setIsCustomDateOpen(false)}
        initialPresetId={modalPresetId}
        initialRange={
          customFrom || customTo
            ? {
                from: customFrom ? new Date(customFrom) : undefined,
                to: customTo ? new Date(customTo) : undefined,
              }
            : null
        }
        onApply={(presetId, nextRange) => {
          setModalPresetId(presetId);
          const iso = (d) => {
            if (!d) return '';
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${day}`;
          };
          if (presetId === 'all') {
            setTimeWindow('all');
            setCustomFrom(''); setCustomTo('');
          } else if (nextRange) {
            // Any concrete preset OR a freeform range → store the
            // resolved dates on the Custom tab. The Custom trigger's
            // label reflects the range so users still see which window
            // is active.
            setTimeWindow('custom');
            setCustomFrom(iso(nextRange.from));
            setCustomTo(iso(nextRange.to));
          }
          setIsCustomDateOpen(false);
        }}
      />
    </div>
  );
};

export default TaxReports;